import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Paper
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import ConditionRow from './ConditionRow';

const ConditionBuilder = ({ conditions, onChange }) => {
  const [operator, setOperator] = useState(conditions?.operator || 'single');
  const [conditionList, setConditionList] = useState(() => {
    // When operator is 'single', use criteria; otherwise use conditions array
    // Note: Empty arrays are truthy in JS, so we must check length
    if (conditions?.operator === 'single' && conditions?.criteria) {
      return [conditions.criteria];
    }
    if (conditions?.conditions?.length > 0) {
      // Database may have nested structure: [{ operator: 'single', criteria: {...} }, ...]
      // We need to unwrap to flat criteria objects for ConditionRow
      return conditions.conditions.map(c => {
        // If it's a nested single condition, extract the criteria
        if (c.operator === 'single' && c.criteria) {
          return c.criteria;
        }
        // Otherwise return as-is (already flat criteria)
        return c;
      });
    }
    if (conditions?.criteria) {
      return [conditions.criteria];
    }
    return [];
  });

  useEffect(() => {
    // Sync changes back to parent
    if (operator === 'single' && conditionList.length > 0) {
      onChange({
        operator: 'single',
        criteria: conditionList[0]
      });
    } else {
      onChange({
        operator,
        conditions: conditionList
      });
    }
  }, [operator, conditionList]);

  const handleAddCondition = () => {
    setConditionList([
      ...conditionList,
      {
        type: 'flag_present',
        domain: 'depression',
        operator: '==',  // Required by Mongoose schema
        value: true
      }
    ]);
  };

  const handleRemoveCondition = (index) => {
    setConditionList(conditionList.filter((_, i) => i !== index));
  };

  const handleConditionChange = (index, newCondition) => {
    const updated = [...conditionList];
    updated[index] = newCondition;
    setConditionList(updated);
  };

  const handleOperatorChange = (newOperator) => {
    setOperator(newOperator);
    // If switching to single and we have multiple conditions, keep only the first
    if (newOperator === 'single' && conditionList.length > 1) {
      setConditionList([conditionList[0]]);
    }
    // If switching from single, ensure we have at least one condition
    if (newOperator !== 'single' && conditionList.length === 0) {
      setConditionList([{
        type: 'flag_present',
        domain: 'depression',
        operator: '==',  // Required by Mongoose schema
        value: true
      }]);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Condition Logic</InputLabel>
        <Select
          value={operator}
          onChange={(e) => handleOperatorChange(e.target.value)}
          label="Condition Logic"
          size="small"
        >
          <MenuItem value="single">Single Condition</MenuItem>
          <MenuItem value="and">All Conditions Must Match (AND)</MenuItem>
          <MenuItem value="or">Any Condition Can Match (OR)</MenuItem>
        </Select>
      </FormControl>

      {conditionList.map((condition, index) => (
        <Box key={index} sx={{ mb: 2 }}>
          {index > 0 && (
            <Divider sx={{ my: 1 }}>
              <Box
                component="span"
                sx={{
                  px: 1,
                  py: 0.5,
                  backgroundColor: operator === 'and' ? '#e3f2fd' : '#fff3e0',
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}
              >
                {operator.toUpperCase()}
              </Box>
            </Divider>
          )}

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <ConditionRow
                condition={condition}
                onChange={(newCondition) => handleConditionChange(index, newCondition)}
              />
            </Box>

            {(operator !== 'single' || conditionList.length > 1) && (
              <IconButton
                size="small"
                color="error"
                onClick={() => handleRemoveCondition(index)}
              >
                <Delete />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}

      {operator !== 'single' && (
        <Button
          startIcon={<Add />}
          onClick={handleAddCondition}
          fullWidth
          variant="outlined"
          size="small"
        >
          Add Condition
        </Button>
      )}
    </Paper>
  );
};

export default ConditionBuilder;
